/**
 * Global application constants
 */

export const TIME_ZONES = [
    "PST - Pacific Time",
    "MST - Mountain Time",
    "CST - Central Time",
    "EST - Eastern Time",
    "UTC-7",
    "UTC-5",
    "UTC-4 with DST (BR)",
    "UTC-3",
    "UTC-3 with DST (BR)",
    "UTC-2",
    "WET - Western European Time",
    "CET - Central European Time",
    "UTC+2",
    "UTC+2 - Moscow Time",
    "UTC+8",
    "UTC+9",
    "UTC"
];

export const US_STATES = [
    { id: 1, code: 'AL', name: 'Alabama' },
    { id: 2, code: 'AK', name: 'Alaska' },
    { id: 3, code: 'AZ', name: 'Arizona' },
    { id: 4, code: 'AR', name: 'Arkansas' },
    { id: 5, code: 'CA', name: 'California' },
    { id: 6, code: 'CO', name: 'Colorado' },
    { id: 7, code: 'CT', name: 'Connecticut' },
    { id: 8, code: 'DE', name: 'Delaware' },
    { id: 9, code: 'FL', name: 'Florida' },
    { id: 10, code: 'GA', name: 'Georgia' },
    { id: 11, code: 'HI', name: 'Hawaii' },
    { id: 12, code: 'ID', name: 'Idaho' },
    { id: 13, code: 'IL', name: 'Illinois' },
    { id: 14, code: 'IN', name: 'Indiana' },
    { id: 15, code: 'IA', name: 'Iowa' },
    { id: 16, code: 'KS', name: 'Kansas' },
    { id: 17, code: 'KY', name: 'Kentucky' },
    { id: 18, code: 'LA', name: 'Louisiana' },
    { id: 19, code: 'ME', name: 'Maine' },
    { id: 20, code: 'MD', name: 'Maryland' },
    { id: 21, code: 'MA', name: 'Massachusetts' },
    { id: 22, code: 'MI', name: 'Michigan' },
    { id: 23, code: 'MN', name: 'Minnesota' },
    { id: 24, code: 'MS', name: 'Mississippi' },
    { id: 25, code: 'MO', name: 'Missouri' },
    { id: 26, code: 'MT', name: 'Montana' },
    { id: 27, code: 'NE', name: 'Nebraska' },
    { id: 28, code: 'NV', name: 'Nevada' },
    { id: 29, code: 'NH', name: 'New Hampshire' },
    { id: 30, code: 'NJ', name: 'New Jersey' },
    { id: 31, code: 'NM', name: 'New Mexico' },
    { id: 32, code: 'NY', name: 'New York' },
    { id: 33, code: 'NC', name: 'North Carolina' },
    { id: 34, code: 'ND', name: 'North Dakota' },
    { id: 35, code: 'OH', name: 'Ohio' },
    { id: 36, code: 'OK', name: 'Oklahoma' },
    { id: 37, code: 'OR', name: 'Oregon' },
    { id: 38, code: 'PA', name: 'Pennsylvania' },
    { id: 39, code: 'RI', name: 'Rhode Island' },
    { id: 40, code: 'SC', name: 'South Carolina' },
    { id: 41, code: 'SD', name: 'South Dakota' },
    { id: 42, code: 'TN', name: 'Tennessee' },
    { id: 43, code: 'TX', name: 'Texas' },
    { id: 44, code: 'UT', name: 'Utah' },
    { id: 45, code: 'VT', name: 'Vermont' },
    { id: 46, code: 'VA', name: 'Virginia' },
    { id: 47, code: 'WA', name: 'Washington' },
    { id: 48, code: 'WV', name: 'West Virginia' },
    { id: 49, code: 'WI', name: 'Wisconsin' },
    { id: 50, code: 'WY', name: 'Wyoming' }
];

export const TITLES = [
    { id: 1, label: "Dr." },
    { id: 2, label: "Mr." },
    { id: 3, label: "Mrs." },
    { id: 4, label: "Ms." }
];

/**
 * Normalizes legacy or raw timezone strings to the new standardized format
 * @param {string} tz - The timezone string from the database
 * @returns {string} The standardized timezone string, or empty string if no match
 */
/** ForaCare PTZ codes stored on legacy rows — keep in sync with `backend/utils/patientTimeZone.js`. */
const FORACARE_TZ_TO_UI = {
    T0001: 'EST - Eastern Time',
    T0002: 'CST - Central Time',
    T0003: 'MST - Mountain Time',
    T0004: 'PST - Pacific Time',
    T0005: 'UTC',
    T0006: 'UTC'
};

export const normalizeTimeZone = (tz) => {
    if (!tz) return '';

    const trimmed = String(tz).trim();
    if (!trimmed) return '';

    if (TIME_ZONES.includes(trimmed)) return trimmed;
    const lowerTrim = trimmed.toLowerCase();
    for (const z of TIME_ZONES) {
        if (z.toLowerCase() === lowerTrim) return z;
    }

    const upper = trimmed.toUpperCase();
    if (/^T\d{4}$/.test(upper) && FORACARE_TZ_TO_UI[upper]) {
        return FORACARE_TZ_TO_UI[upper];
    }

    const lowerTz = lowerTrim;

    if (lowerTz.includes('eastern') || lowerTz === 'est' || lowerTz.includes('new_york') || lowerTz.includes('america/new_york')) return 'EST - Eastern Time';
    if (lowerTz.includes('central') || lowerTz === 'cst' || lowerTz.includes('chicago') || lowerTz.includes('america/chicago')) return 'CST - Central Time';
    if (lowerTz.includes('mountain') || lowerTz === 'mst' || lowerTz.includes('denver') || lowerTz.includes('america/denver')) return 'MST - Mountain Time';
    if (lowerTz.includes('pacific') || lowerTz === 'pst' || lowerTz.includes('los_angeles') || lowerTz.includes('america/los_angeles')) return 'PST - Pacific Time';

    if (lowerTz === 'wet' || lowerTz.includes('western european') || lowerTz.includes('europe/london')) return 'WET - Western European Time';
    if (lowerTz === 'cet' || lowerTz.includes('central european') || lowerTz.includes('europe/paris')) return 'CET - Central European Time';
    if (lowerTz.includes('moscow') || lowerTz.includes('europe/moscow')) return 'UTC+2 - Moscow Time';

    if (/^utc[+-]/i.test(trimmed)) {
        for (const z of TIME_ZONES) {
            if (z.toLowerCase() === lowerTrim) return z;
        }
    }
    if (/^gmt[+-]/i.test(trimmed)) {
        const asUtc = trimmed.replace(/^gmt/i, 'UTC');
        for (const z of TIME_ZONES) {
            if (z.toLowerCase() === asUtc.toLowerCase()) return z;
        }
    }

    if (lowerTz === 'utc' || lowerTz === 'gmt' || lowerTz === 'etc/utc' || lowerTz === 'etc/gmt') return 'UTC';

    return '';
};
